// 수학 출제하기의 비율 막대(2026-11-05 대표: 최상 몇 % 처럼 퍼센트로, 합은 100%).
// 막대 하나를 칸으로 나누고 칸 사이 손잡이를 끌어 비율을 바꾼다. 손잡이로 나누므로 합은 늘 100 이고 5% 단위다.
// 난이도 · 단원 · 소분류가 같은 막대를 쓴다. 서버로는 difficulty_ratio · unit_ratio · skill_ratio 로 간다(수학 사이트 8절).
import React, { useRef } from "react";

export const DIFFICULTY_LIST = ["최하", "하", "중", "상", "최상"];
export const DEFAULT_DIFFICULTY_RATIO = { 최하: 10, 하: 20, 중: 40, 상: 20, 최상: 10 };
const STEP = 5;
const TONE_COUNT = 5;

// 칸 수대로 고르게(5% 단위, 남는 몫은 앞 칸부터)
export const evenRatioOf = (keyList) => {
  if (!keyList.length) return {};
  const unitCount = 100 / STEP;
  const base = Math.floor(unitCount / keyList.length);
  let rest = unitCount - base * keyList.length;
  return Object.fromEntries(
    keyList.map((key) => {
      const extra = rest > 0 ? 1 : 0;
      rest -= extra;
      return [key, (base + extra) * STEP];
    })
  );
};

// 지금 비율이 이 칸 목록과 맞으면 그대로, 칸이 바뀌었으면 고르게 다시 나눈다
export const ratioForKeyList = (ratio, keyList) => {
  const ratioKeyList = Object.keys(ratio || {});
  const isSame = ratioKeyList.length === keyList.length && keyList.every((key) => key in ratio);
  return isSame ? ratio : evenRatioOf(keyList);
};

// 문항 수를 비율대로 나눈 값(끝수는 나머지가 큰 쪽부터). 칸 아래 'N문항' 으로만 보여 준다
const countListOf = (total, percentList) => {
  const exactList = percentList.map((percent) => (total * percent) / 100);
  const countList = exactList.map(Math.floor);
  let rest = total - countList.reduce((sum, one) => sum + one, 0);
  percentList
    .map((_, index) => index)
    .sort((left, right) => exactList[right] - countList[right] - (exactList[left] - countList[left]))
    .forEach((index) => {
      if (rest > 0) {
        countList[index] += 1;
        rest -= 1;
      }
    });
  return countList;
};

const toneOf = (index, count) => (count <= 1 ? 2 : Math.round((index * (TONE_COUNT - 1)) / (count - 1)));

const MathBankRatioBar = ({ itemList, ratio, onChange, total, label }) => {
  const barRef = useRef(null);
  const dragRef = useRef(null);
  const percentList = itemList.map((item) => ratio[item.key] || 0);
  const boundaryList = [];
  percentList.slice(0, -1).reduce((sum, percent) => {
    boundaryList.push(sum + percent);
    return sum + percent;
  }, 0);
  const countList = countListOf(total, percentList);

  const percentAt = (clientX) => {
    const rect = barRef.current.getBoundingClientRect();
    const percent = ((clientX - rect.left) / rect.width) * 100;
    return Math.max(0, Math.min(100, Math.round(percent / STEP) * STEP));
  };

  const moveBoundary = (index, percent) => {
    const low = index > 0 ? boundaryList[index - 1] : 0;
    const high = index < boundaryList.length - 1 ? boundaryList[index + 1] : 100;
    const nextList = [...boundaryList];
    nextList[index] = Math.max(low, Math.min(high, percent));
    if (nextList[index] === boundaryList[index]) return;
    const edgeList = [0, ...nextList, 100];
    onChange(Object.fromEntries(itemList.map((item, itemIndex) => [item.key, edgeList[itemIndex + 1] - edgeList[itemIndex]])));
  };

  // 누른 자리에서 가장 가까운 손잡이를 잡는다. 여러 개가 겹쳐 있으면 처음 끄는 방향으로 고른다
  const handlePointerDown = (event) => {
    if (event.button !== 0 || !boundaryList.length) return;
    const percent = percentAt(event.clientX);
    const distanceList = boundaryList.map((one) => Math.abs(one - percent));
    const nearest = Math.min(...distanceList);
    const candidateList = distanceList.map((one, index) => (one === nearest ? index : -1)).filter((index) => index >= 0);
    dragRef.current = { candidateList, index: candidateList.length === 1 ? candidateList[0] : null, startX: event.clientX };
    barRef.current.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.index === null) {
      if (Math.abs(event.clientX - drag.startX) < 3) return;
      drag.index = event.clientX > drag.startX ? drag.candidateList[drag.candidateList.length - 1] : drag.candidateList[0];
    }
    moveBoundary(drag.index, percentAt(event.clientX));
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  const handleKeyDown = (index) => (event) => {
    const delta = { ArrowLeft: -STEP, ArrowDown: -STEP, ArrowRight: STEP, ArrowUp: STEP }[event.key];
    if (!delta) return;
    event.preventDefault();
    moveBoundary(index, boundaryList[index] + delta);
  };

  return (
    <div className="math-bank-page__ratio">
      <div
        ref={barRef}
        className="math-bank-page__ratio-bar"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {itemList.map((item, index) => (
          <div
            key={item.key}
            className={`math-bank-page__ratio-segment math-bank-page__ratio-tone--${toneOf(index, itemList.length)}`}
            style={{ width: `${percentList[index]}%` }}
          />
        ))}
        {boundaryList.map((percent, index) => (
          <button
            key={itemList[index].key}
            type="button"
            role="slider"
            aria-label={`${label} ${itemList[index].label} · ${itemList[index + 1].label} 경계`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            className="math-bank-page__ratio-handle"
            style={{ left: `${percent}%` }}
            onKeyDown={handleKeyDown(index)}
          />
        ))}
      </div>
      <div
        className={`math-bank-page__ratio-legend${itemList.length > TONE_COUNT ? " math-bank-page__ratio-legend--list" : ""}`}
        style={itemList.length > TONE_COUNT ? undefined : { gridTemplateColumns: `repeat(${itemList.length}, minmax(0, 1fr))` }}
      >
        {itemList.map((item, index) => (
          <div
            key={item.key}
            className={`math-bank-page__ratio-item${percentList[index] ? "" : " math-bank-page__ratio-item--zero"}`}
          >
            <span className={`math-bank-page__ratio-swatch math-bank-page__ratio-tone--${toneOf(index, itemList.length)}`} />
            <span className="math-bank-page__ratio-name" title={item.label}>
              {item.label}
            </span>
            <span className="math-bank-page__ratio-percent">{percentList[index]}%</span>
            <span className="math-bank-page__ratio-count">{countList[index]}문항</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MathBankRatioBar;
